/**
 * useArTablePaginationCursors
 *
 * Stores the last DocumentSnapshot of each page for cursor-based pagination.
 * Uses Valtio subscribe to capture cursors when rows state changes,
 * avoiding the extra render cycle of a useEffect dependency on snapshots.
 *
 * Invariant: `byPage` only ever holds snapshots produced under
 * `paginationCursors.orderByKey`. `useArTablePagination` re-keys the cache
 * while rendering the sort click, a paint before `StoreProvider` tears the old
 * rows listener down, so a delivery queued under the superseded ordering can
 * still land here. It is rejected by comparing the delivering persist's own
 * `orderByKey` with the cache's — never by timing (SYD-2304).
 */

import { useContext, useEffect } from 'react';
import { subscribe } from 'valtio';
import type { QuerySnapshot } from 'firebase/firestore';
import { useStoreNonReactive } from '../../../store';
import type { CollectionPersist, CollectionState } from '../../../store';
import { ArTableUIStateContext } from '../context';
import { AR_TABLE_AGGREGATIONS_ROW_ID } from '#types/System.Artifacts.types';
import type { ArRowData } from '#types/System.Artifacts.types';

function useArTablePaginationCursors(): void {
  const { state: rowsStateRaw, persist: rowsPersistRaw } = useStoreNonReactive('rows');
  const rowsState = rowsStateRaw as CollectionState<ArRowData>;
  const rowsPersist = rowsPersistRaw as CollectionPersist<ArRowData>;
  const uiState = useContext(ArTableUIStateContext)!;

  useEffect(() => {
    let lastProcessedSnap: QuerySnapshot | null = null;

    return subscribe(rowsState, () => {
      if (rowsState.loading) return;
      if (rowsPersist.orderByKey !== uiState.paginationCursors.orderByKey) return;

      const snap = rowsPersist.getLastQuerySnapshot();
      if (!snap || snap === lastProcessedSnap) return;
      lastProcessedSnap = snap;

      const dataDocs = snap.docs.filter(
        (d) => d.id !== AR_TABLE_AGGREGATIONS_ROW_ID
      );
      if (dataDocs.length === 0) return;

      uiState.paginationCursors.byPage.set(
        uiState.pagination.currentPage,
        dataDocs[dataDocs.length - 1]
      );
    });
  }, [rowsState, rowsPersist, uiState]);
}

export { useArTablePaginationCursors };
